import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import { Box, Tooltip, Typography } from '@saviynt/design-system';
import moment from 'moment';
import PropTypes from 'prop-types';

import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';
import ARSAccordian from '../ARSAccordian';
import ARSTimeLineIconLarge from '../ARSTimeLineIconLarge';
import ARSTimeLineIconSmall from '../ARSTimeLineIconSmall';
import {
  DATE_FIELDS,
  MIN_COUNT_FOR_TOOLTIP_APPEAR,
  TOOLTIP_DELAY_IN_MS_TILE,
} from '../constant';

import { invertedMessages } from './messages';

function ARSApproverTile({
  approverTileData,
  taskSectionTileData,
  globalDateFormat,
}) {
  if (!approverTileData) return null;

  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user);

  const l = useInvertedLocalize(invertedMessages);
  const {
    accessApproverSectionLabelKey,
    // accessApproverSectionLabelValue,
    reviewMethodLabelKey,
    // reviewMethodLabelValue,
    accessApproversSectionLabelKey,
    reviewMethodMessageKey,
    // accessApproversSectionLabelValue,
    accessApprovers,
    messageKeys,
  } = approverTileData;

  const accessApproversKeyMap = Object?.keys(accessApprovers);
  const shouldRenderApproversList = Boolean(accessApproversKeyMap?.length > 0);
  const assignedToList = accessApproversKeyMap?.reduce((total, item) => {
    const temp = accessApprovers?.[item]?.accessApprovers?.assignedTo || [];
    const tempSanitzed = temp?.map((name) => {
      if (name.startsWith('(') && name.endsWith(')')) {
        const sanitizedName = name.substring(1, name.length - 2);

        return sanitizedName;
      }

      return name;
    });

    return [...total, ...tempSanitzed];
  }, []);

  const shouldShowAssignedToList = Boolean(
    reviewMethodLabelKey !== 'AutoApprove'
  );

  return (
    <ARSAccordian>
      <ARSTimeLineIconLarge tail={taskSectionTileData ? 'fill' : 'none'} />
      <Box tag='div' className='AccessRequestHistoryBody-accordianBox'>
        <Typography
          kind='h3'
          className='AccessRequestHistoryBody-accordianBoxTitle'>
          {messages[accessApproverSectionLabelKey] ||
            localize(intl, getMessageKey(accessApproverSectionLabelKey))}
        </Typography>
        <Box tag='div' className='AccessRequestHistoryBody-accordianBoxBody'>
          <div
            style={{
              width: shouldShowAssignedToList ? 'calc(25% - .75rem)' : '',
            }}
            className='AccessRequestHistoryBody-accordianBoxBodyTile'>
            <Typography kind='body3'>
              {messages[reviewMethodMessageKey] ||
                localize(intl, getMessageKey(reviewMethodMessageKey))}
            </Typography>
            <Typography
              kind='body2'
              className='AccessRequestHistoryBody-accordianBoxBodyHighlighted'>
              {l(reviewMethodLabelKey)}
            </Typography>
          </div>
          {shouldShowAssignedToList && (
            <div
              style={{ width: '75%' }}
              className='AccessRequestHistoryBody-accordianBoxBodyTile'>
              <Typography kind='body3'>
                {messages[messageKeys?.taskOwner] ||
                  localize(intl, getMessageKey(messageKeys?.taskOwner))}
              </Typography>
              <Typography
                kind='body2'
                className='AccessRequestHistoryBody-accordianBoxBodyHighlighted'>
                {assignedToList?.length > MIN_COUNT_FOR_TOOLTIP_APPEAR ? (
                  <>
                    {assignedToList
                      .slice(0, MIN_COUNT_FOR_TOOLTIP_APPEAR)
                      ?.map((item) => (
                        <span className='AccessRequestHistoryModalSubTitle-textBoxValue'>
                          {`${item}, `}
                        </span>
                      ))}
                    <Tooltip
                      openDelay={TOOLTIP_DELAY_IN_MS_TILE}
                      text={assignedToList?.join(', ')}
                      maxWidth='20rem'
                      placement='top-end'
                      trigger={
                        <span className='AccessRequestHistoryBody-accordianBoxBodyHighlighted--withToolTip'>
                          {`+${
                            assignedToList.length - MIN_COUNT_FOR_TOOLTIP_APPEAR
                          } more`}
                        </span>
                      }
                    />
                  </>
                ) : (
                  assignedToList?.map((item, index) => (
                    <span className='AccessRequestHistoryModalSubTitle-textBoxValue'>
                      {`${item}${
                        index === assignedToList.length - 1 ? '' : ', '
                      }`}
                    </span>
                  ))
                )}
              </Typography>
            </div>
          )}
        </Box>
        <ARSAccordian className='subbody'>
          <ARSTimeLineIconSmall tail='none' />
          <Box
            tag='div'
            className='AccessRequestHistoryBody-accordianBox--subbox'>
            <Typography
              kind='h3'
              className='AccessRequestHistoryBody-accordianBoxTitle--subbody'>
              {messages[accessApproversSectionLabelKey] ||
                localize(intl, getMessageKey(accessApproversSectionLabelKey))}
            </Typography>
            {shouldRenderApproversList && (
              <Box
                tag='div'
                className='AccessRequestHistoryBody-accordianBoxSubBody--subbody'>
                {accessApproversKeyMap?.map((accessApproverKey, index) => {
                  const approverKeyData =
                    accessApprovers?.[accessApproverKey]?.accessApprovers;
                  const approverKeyDataMap = Object?.keys(approverKeyData);

                  const showReviewMethodHeaderLabel =
                    accessApprovers?.[accessApproverKey]
                      ?.showReviewMethodHeaderLabel;
                  const reviewMethodHeaderLabelKey1 =
                    accessApprovers?.[accessApproverKey]
                      ?.reviewMethodHeaderLabelKey1;
                  const reviewMethodHeaderLabelKey2 =
                    accessApprovers?.[accessApproverKey]
                      ?.reviewMethodHeaderLabelKey2;
                  const reviewMethodHeaderLabelValue =
                    accessApprovers?.[accessApproverKey]
                      ?.reviewMethodHeaderLabelValue;

                  return approverKeyDataMap?.length > 0 ? (
                    <>
                      {showReviewMethodHeaderLabel && (
                        <Typography
                          kind='body2-bold'
                          className='AccessRequestHistoryBody-accordianBoxTitle--subbody'>
                          {reviewMethodHeaderLabelKey1 &&
                          reviewMethodHeaderLabelKey2
                            ? `${
                                messages[reviewMethodHeaderLabelKey1] ||
                                localize(
                                  intl,
                                  getMessageKey(reviewMethodHeaderLabelKey1)
                                )
                              } · ${
                                messages[reviewMethodHeaderLabelKey2] ||
                                localize(
                                  intl,
                                  getMessageKey(reviewMethodHeaderLabelKey2)
                                )
                              }`
                            : `${reviewMethodHeaderLabelValue}`}
                        </Typography>
                      )}
                      <Box
                        tag='div'
                        key={accessApproverKey}
                        className={`AccessRequestHistoryBody-accordianBoxSubBody--subbodyList ${
                          showReviewMethodHeaderLabel ? 'shifted' : ''
                        } ${
                          index === accessApproversKeyMap.length - 1
                            ? 'lastItem'
                            : ''
                        }`}>
                        {approverKeyDataMap?.map((key) => {
                          let fieldValue = approverKeyData?.[key];

                          if (DATE_FIELDS.includes(key)) {
                            fieldValue = moment
                              .utc(fieldValue)
                              .local()
                              .format(
                                `${globalDateFormat?.toUpperCase()} hh:mm:ss`
                              );
                          }

                          if (Array.isArray(fieldValue)) {
                            fieldValue =
                              fieldValue?.length >
                              MIN_COUNT_FOR_TOOLTIP_APPEAR ? (
                                <>
                                  {fieldValue
                                    .slice(0, MIN_COUNT_FOR_TOOLTIP_APPEAR)
                                    ?.map((item) => (
                                      <span className='AccessRequestHistoryModalSubTitle-textBoxValue'>
                                        {`${item}, `}
                                      </span>
                                    ))}
                                  <Tooltip
                                    openDelay={TOOLTIP_DELAY_IN_MS_TILE}
                                    text={fieldValue?.join(', ')}
                                    maxWidth='20rem'
                                    placement='top-end'
                                    trigger={
                                      <span className='AccessRequestHistoryBody-accordianBoxBodyHighlighted--withToolTip'>
                                        {`+${
                                          fieldValue.length -
                                          MIN_COUNT_FOR_TOOLTIP_APPEAR
                                        } more`}
                                      </span>
                                    }
                                  />
                                </>
                              ) : (
                                fieldValue?.map((item, idx) => (
                                  <span className='AccessRequestHistoryModalSubTitle-textBoxValue'>
                                    {`${item}${
                                      idx === fieldValue.length - 1 ? '' : ', '
                                    }`}
                                  </span>
                                ))
                              );
                          }

                          return (
                            <React.Fragment key={`${accessApproverKey}-${key}`}>
                              {!showReviewMethodHeaderLabel ? (
                                <>
                                  {key !== 'approveMethod' && (
                                    <span className='AccessRequestHistoryModalSubTitle-textBoxKey'>
                                      {`${
                                        messages[messageKeys?.[key]] ||
                                        localize(
                                          intl,
                                          getMessageKey(messageKeys?.[key])
                                        )
                                      }:`}
                                      &nbsp;
                                    </span>
                                  )}
                                  {Array.isArray(approverKeyData?.[key]) ? (
                                    fieldValue
                                  ) : (
                                    <span className='AccessRequestHistoryModalSubTitle-textBoxValue'>
                                      {fieldValue}
                                    </span>
                                  )}
                                  <span className='AccessRequestHistoryModalSubTitle-textBoxTerminate' />
                                </>
                              ) : (
                                key !== 'approveMethod' && (
                                  <>
                                    <span className='AccessRequestHistoryModalSubTitle-textBoxKey'>
                                      {`${
                                        messages[messageKeys?.[key]] ||
                                        localize(
                                          intl,
                                          getMessageKey(messageKeys?.[key])
                                        )
                                      }:`}
                                      &nbsp;
                                    </span>
                                    {Array.isArray(approverKeyData?.[key]) ? (
                                      fieldValue
                                    ) : (
                                      <span className='AccessRequestHistoryModalSubTitle-textBoxValue'>
                                        {fieldValue}
                                      </span>
                                    )}
                                    <span className='AccessRequestHistoryModalSubTitle-textBoxTerminate' />
                                  </>
                                )
                              )}
                            </React.Fragment>
                          );
                        })}
                      </Box>
                    </>
                  ) : null;
                })}
              </Box>
            )}
          </Box>
        </ARSAccordian>
      </Box>
    </ARSAccordian>
  );
}

ARSApproverTile.propTypes = {
  approverTileData: PropTypes.shape({
    accessApproverSectionLabelKey: PropTypes.string,
    accessApproverSectionLabelValue: PropTypes.string,
    reviewMethodLabelKey: PropTypes.string,
    reviewMethodLabelValue: PropTypes.string,
    reviewMethodMessageKey: PropTypes.string,
    reviewMethod: PropTypes.string,
    accessApproversSectionLabelKey: PropTypes.string,
    accessApproversSectionLabelValue: PropTypes.string,
    accessApprovers: PropTypes.shape({}),
    messageKeys: PropTypes.shape({ taskOwner: PropTypes.string }),
  }).isRequired,
  taskSectionTileData: PropTypes.shape({}).isRequired,
  globalDateFormat: PropTypes.string.isRequired,
};

export default ARSApproverTile;
